import { useState } from "react";
import { createFileRoute, redirect, Outlet } from "@tanstack/react-router";
import { appEnv, fetchSession, sessionQueryKey } from "@/lib/auth";
import { BreadcrumbProvider } from "@/components/shell/BreadcrumbContext";
import { EnvBanner } from "@/components/shell/EnvBanner";
import { Sidebar } from "@/components/shell/Sidebar";
import { Topbar } from "@/components/shell/Topbar";

// Protected layout. The beforeLoad guard ensures a session before any child
// route loads; unauthenticated users are redirected to /login.
export const Route = createFileRoute("/_shell")({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData({
      queryKey: sessionQueryKey,
      queryFn: fetchSession,
    });
    if (!session) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
    return { session };
  },
  component: ShellLayout,
});

const COLLAPSE_KEY = "dtw-sidebar-collapsed";

function ShellLayout() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });

  function toggleCollapse() {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* privacy mode */
      }
      return next;
    });
  }

  const showEnvBanner = appEnv() !== "production";

  return (
    <BreadcrumbProvider>
      <div
        className={`app${collapsed ? " is-collapsed" : ""}${showEnvBanner ? " has-env-banner" : ""}`}
      >
        {showEnvBanner && <EnvBanner />}
        <Sidebar collapsed={collapsed} onToggleCollapse={toggleCollapse} />
        <Topbar />
        <main className="app-main">
          <Outlet />
        </main>
      </div>
    </BreadcrumbProvider>
  );
}
